import { Headline3, Meta, Spinner, theme } from 'aimo-ui';
import PermitAvailability from 'components/longTermParking/waitingList/PermitAvailability';
import { differenceInMinutes } from 'date-fns';
import usePollSalesOrderStatus from 'hooks/usePollSalesOrderStatus';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/native';
import { ColumnWithMargins, StyledImage } from 'styles/ContainerStyles';
import { ParkzonePmcUnion } from 'types/common';
import { CountryCode } from 'types/generatedSchemaTypes';
import { currencyFormatter, getPriceForSalesOrder } from 'utils/commonUtils';
import {
  durationText,
  getEffectiveVisibility,
  isItemAccessPermit,
  isItemOwned,
  isItemPmc,
  isItemPredefinedTicket,
  isItemSalesOrder,
  isPmcAvailable,
  isWaitingListItem,
} from 'utils/permitUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { Row } from '../styles/styles';

const Wrapper = styled.TouchableOpacity<{
  isHorizontalList?: boolean;
  notAvailable: boolean;
  disabled?: boolean;
}>`
  background-color: ${theme.colors.lightBg.green};
  border-radius: 20px;
  padding: 12px 10px;
  ${({ isHorizontalList }) =>
    isHorizontalList ? 'max-width: 300px' : undefined};
  ${({ notAvailable, disabled }) =>
    notAvailable || disabled ? 'opacity: 0.5' : undefined};
`;

type PrepaidItemProps = {
  item: ParkzonePmcUnion;
  countryCode: CountryCode;
  testID?: string;
  onPress?: (params?: any) => void;
  isHorizontalList?: boolean;
  pmcIsLockedForTheCustomer?: boolean;
  pmcIsInsideALockedMemberGroup?: boolean;
  disabled?: boolean;
};

const PermitImage = ({ testID }: { testID?: string }) => (
  <StyledImage
    source={require('assets/images/permit.png')}
    width={62}
    height={53}
    center
    testID={testID}
  />
);

const PrepaidTicketOrPermitItem = ({
  item,
  countryCode,
  testID,
  onPress,
  isHorizontalList,
  pmcIsLockedForTheCustomer,
  pmcIsInsideALockedMemberGroup,
  disabled,
}: PrepaidItemProps) => {
  const { t } = useTranslation();

  const handleOnPress = () => {
    onPress?.(item);
  };

  const salesOrder = isItemSalesOrder(item) ? item : undefined;
  const isUpdating = salesOrder?.orderStatus === 'UPDATING';
  usePollSalesOrderStatus(
    salesOrder?.id as string,
    salesOrder?.orderStatus ?? 'ACTIVE'
  );

  const showServiceFee = () => {
    if (isItemPredefinedTicket(item)) {
      return item?.serviceFee !== 0 && typeof item?.serviceFee === 'number'
        ? `+ ${currencyFormatter(item?.serviceFee, countryCode)} ${t(
            'parking.receipt.serviceFee'
          )}`
        : null;
    }
  };

  const pricingText = () => {
    if (isItemPmc(item)) {
      const effectiveVisibility = getEffectiveVisibility(
        pmcIsLockedForTheCustomer,
        item.visibility,
        pmcIsInsideALockedMemberGroup
      );
      if (effectiveVisibility === 'LOCKED') {
        return t('permit.unlockPMC.unlockToSeePrice');
      }
      return getPriceForSalesOrder(item.prices, countryCode).totalPrice;
    }

    if (isItemAccessPermit(item) || isItemSalesOrder(item)) {
      return '';
    }

    if (isWaitingListItem(item)) {
      return item.orderStatus === 'WAITING_FOR_CSR_APPROVAL'
        ? t('longTermParking.waitingList.inReview')
        : t('longTermParking.waitingList.onWaitingList');
    }

    return currencyFormatter((item?.price as number) || 0, countryCode);
  };

  const itemName = (listItem: ParkzonePmcUnion) => {
    if (isItemPmc(listItem)) {
      return listItem.name;
    }
    if (isItemAccessPermit(listItem)) {
      return listItem.pmcName;
    }
    if (isItemSalesOrder(listItem)) {
      return listItem?.orderLines?.[0]?.pmc?.name || '';
    }
    if (isWaitingListItem(listItem)) {
      return listItem.pmcName;
    }
    const duration = durationText(item);
    if (duration) {
      return `${duration} ${listItem?.name || ''}`;
    }
    return listItem?.name || '';
  };

  const isExpiringSoon =
    isItemPredefinedTicket(item) &&
    differenceInMinutes(new Date(item?.endTime ?? ''), new Date()) <= 30;

  const pmcNotAvailable =
    isItemPmc(item) && isPmcAvailable(item).permitNotAvailable;

  return (
    <Wrapper
      {...testIdentifiers(testID as string)}
      isHorizontalList={isHorizontalList}
      notAvailable={pmcNotAvailable}
      disabled={disabled}
      onPress={isUpdating ? undefined : handleOnPress}>
      <Row wrap={false}>
        <PermitImage testID={`${testID}-image`} />
        <ColumnWithMargins style={{ flex: 1 }}>
          <Meta
            color={isExpiringSoon ? '#F50000' : undefined}
            numberOfLines={2}>
            {itemName(item)}
          </Meta>
          <Headline3 {...testIdentifiers(`${testID}-price`)}>
            {isUpdating && <Spinner size={25} />}
            {isItemOwned(item) ? t('button.manage') : pricingText()}
          </Headline3>
          {isItemPredefinedTicket(item) && !isItemOwned(item) && (
            <Meta testID={`${testID}-serviceFee`}>{showServiceFee()}</Meta>
          )}
        </ColumnWithMargins>
        {isItemPmc(item) && (
          <PermitAvailability
            pmc={item}
            pmcIsLockedForTheCustomer={pmcIsLockedForTheCustomer}
            pmcIsInsideALockedMemberGroup={pmcIsInsideALockedMemberGroup}
          />
        )}
      </Row>
    </Wrapper>
  );
};

export default PrepaidTicketOrPermitItem;
